import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { DialogActions, DialogContent } from '@mui/material';
import Typography from '@mui/material/Typography';

import {
  handleBackNavAction,
  updateErrorAction,
  updateStageAction,
} from 'app-components/flux/transaction-rules/transactionRuleActions';
import { useManager } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';
import { useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import {
  CLEARED,
  EDITING,
  RESOLVING,
  SUBMITTING,
  VALIDATED,
} from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { cancelTransactionRuleDialog } from 'components/Dialogs/CancelTransactionRule/actions';
import { deleteTransactionRuleDialog } from 'components/Dialogs/DeleteTransactionRule/actions';
import StdDialog from 'components/Dialogs/StdDialog';
import SettingsDrawer from 'components/Drawers/Settings';

import RuleEditor from './RuleEditor';
import RuleFooter from './RuleFooter';
import RuleReview from './RuleReview';

const FormContainer: React.FC = () => {
  const { state, dispatch } = useStateContext();
  const { stage, rule, onUpdate, backText, titleText, fromTxn } = state;
  const manager = useManager();
  const rootDispatch = useDispatch();

  const handleSubmit = useCallback(() => {
    if (stage === EDITING || stage === RESOLVING) {
      manager.submit(true); // runs field-level validation
      if (!manager.isFormValid()) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Form fields need attention'));
        return;
      }
    }

    if (stage === VALIDATED) {
      dispatch(updateStageAction(SUBMITTING));
    }
  }, [dispatch, manager, stage]);

  const handleNavigation = useCallback(() => {
    stage && dispatch(handleBackNavAction(stage));
  }, [dispatch, stage]);

  const handleDelete = useCallback(() => {
    rule?.id &&
      rootDispatch(
        deleteTransactionRuleDialog({
          ruleId: rule.id,
          source: 'ruleEdit',
          onDelete: () => dispatch(updateStageAction(CLEARED)),
        }),
      );
  }, [dispatch, rootDispatch, rule]);

  const handleCancel = useCallback(() => {
    // show confirmation dialog on create but not edit
    rule && rule.id
      ? dispatch(updateStageAction(CLEARED))
      : rootDispatch(cancelTransactionRuleDialog({ onCancel: () => dispatch(updateStageAction(CLEARED)) }));
  }, [dispatch, rootDispatch, rule]);

  useEffect(() => {
    if (!stage) {
      rule ? dispatch(updateStageAction(EDITING)) : onUpdate(undefined);
    } else if (stage === 'EDITING' && !rule) {
      onUpdate(undefined);
    } else if (stage === 'SUBMITTING' && rule) {
      // if we have a duplicateId in the state, set the rule id so we treat this as an update
      const newRule = state.duplicateId ? { ...rule, id: state.duplicateId } : rule;
      onUpdate(newRule);
    }
  }, [dispatch, onUpdate, rule, stage, state]);

  const getMainContent = (): React.JSX.Element => {
    return (
      <>
        {(stage === EDITING || stage === RESOLVING) && <RuleEditor />}
        {stage === VALIDATED && rule && <RuleReview />}
      </>
    );
  };

  return (
    <>
      {fromTxn && (
        <StdDialog
          id='rules-transaction-list-dialog'
          onClose={handleCancel}
          size='md'
          showBackButton={stage === VALIDATED}
          onBack={handleNavigation}
          title={titleText}
        >
          <DialogContent>{getMainContent()}</DialogContent>
          <DialogActions>
            <RuleFooter onSubmit={handleSubmit} onCancel={handleCancel} onDelete={handleDelete} />
          </DialogActions>
        </StdDialog>
      )}

      {!fromTxn && (
        <SettingsDrawer
          id='rules-settings-drawer'
          open
          stage={stage || 'EDITING'}
          onClose={handleNavigation}
          header={<Typography variant='headlineMedium'>{titleText}</Typography>}
          footer={<RuleFooter onSubmit={handleSubmit} onCancel={handleCancel} onDelete={handleDelete} />}
          backText={backText}
        >
          {getMainContent()}
        </SettingsDrawer>
      )}
    </>
  );
};

export default FormContainer;
